/**
 * Interview scheduling wizard shell.
 * Composes Header, Content (tabs/stepper), and Footer.
 */
import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Button, getCountriesByDialCode } from '@kula-ai/components';
import { CenterSpinner } from '@/components/ats';
import type { ScheduleInterviewProps } from './config/schedule.types';
import { SCHEDULE_INTERVIEW_COPY } from './config/schedule.copy';
import { useScheduleInterview } from './useScheduleInterview';
import { ScheduleHeader } from './shell/ScheduleHeader';
import { ScheduleContent } from './shell/ScheduleContent';
import { ScheduleFooter } from './shell/ScheduleFooter';

export const ScheduleInterview = ({
  mode,
  apiReference = 'create',
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  onClose,
  onSuccess,
  interviewId,
  scheduleId,
  candidateName,
  candidateAvatar,
  jobTitle,
  applicationData,
}: ScheduleInterviewProps) => {
  const { candidatePhone, candidatePhoneValue } = useMemo(() => {
    // `GET /ats_applications/:id` returns `contact.phone_number` +
    // `contact.phone_code` (not `local_phone_number` — that was a
    // guessed name that silently made the phone look empty). Both are
    // plain strings; `phone_code` may carry a leading `+`.
    const contact = (applicationData?.contact ?? {}) as {
      phone_code?: string;
      phone_number?: string;
    };
    const local = contact.phone_number?.trim() ?? '';
    const code = contact.phone_code?.trim() ?? '';
    if (!local) {
      return { candidatePhone: '', candidatePhoneValue: undefined };
    }
    // PhoneInput keys off `countryCode` (ISO alpha-2) — resolve it from
    // the dial code so the flag + formatting match the stored number.
    const matches = getCountriesByDialCode(code);
    const resolvedCountryCode = matches.length > 0 ? matches[0].code : '';
    return {
      candidatePhone: code ? `${code} ${local}` : local,
      candidatePhoneValue: {
        countryCode: resolvedCountryCode,
        dialCode: code.startsWith('+') ? code : code ? `+${code}` : '',
        number: local,
      },
    };
  }, [applicationData]);

  const {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    showEditLoader,
    showEditError,
    showCreateLoader,
    activeStep,
    isBusy,
    onStepChange,
    goNextOrComplete,
    goBack,
    canGoBack,
    primaryButtonLabel,
    backButtonLabel,
    lastStepKey,
  } = useScheduleInterview({
    mode,
    apiReference,
    applicationId,
    candidateId,
    jobId,
    stageId,
    stageActivityId,
    interviewId,
    scheduleId,
    initialCandidatePhone: candidatePhoneValue,
    onClose,
    onSuccess,
  });

  const isEdit = apiReference === 'edit';
  const headerTitle = isEdit
    ? SCHEDULE_INTERVIEW_COPY.header.edit
    : SCHEDULE_INTERVIEW_COPY.header.create;

  const stepProps = useMemo(
    () => ({
      payloadRef,
      mode,
      apiReference,
      prefillVersion,
      atsJobId: jobId,
      atsJobStageActionId: stageActivityId,
      candidatePhone,
      candidatePhoneValue,
    }),
    [
      payloadRef,
      mode,
      apiReference,
      prefillVersion,
      jobId,
      stageActivityId,
      candidatePhone,
      candidatePhoneValue,
    ]
  );

  if (showEditLoader || showCreateLoader) {
    return (
      <Flex flex="1" minH={0} h="100%" w="100%" align="center" justify="center">
        <CenterSpinner />
      </Flex>
    );
  }

  if (showEditError) {
    return (
      <Flex
        flex="1"
        minH={0}
        h="100%"
        w="100%"
        align="center"
        justify="center"
        flexDirection="column"
        gap="4"
        px="6"
      >
        <Text color="secondary.700" textAlign="center">
          This interview could not be loaded.
        </Text>
        <Button variant="secondary" onClick={() => onClose?.()}>
          Close
        </Button>
      </Flex>
    );
  }

  // Hide the secondary CTA on the final step — the primary button
  // on that step is already the same "Update interview" / "Schedule
  // interview" final action, so rendering both would be a visible
  // duplicate.
  const isOnLastStep = activeStep === lastStepKey;
  const secondaryCta =
    config.showSecondaryCta && !isOnLastStep
      ? {
          label: isEdit
            ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
            : SCHEDULE_INTERVIEW_COPY.footer.scheduleNow,
          onClick: scheduleNow,
        }
      : undefined;

  return (
    <Flex flex="1" flexDirection="column" minH={0} h="100%" w="100%">
      <ScheduleHeader
        title={headerTitle}
        candidateId={candidateId}
        candidateName={candidateName}
        candidateAvatar={candidateAvatar}
        jobTitle={jobTitle}
        applicationData={applicationData}
        stageId={stageId}
      />

      <ScheduleContent
        config={config}
        stepProps={stepProps}
        interviewPlan={interviewPlan}
        candidateName={candidateName}
        candidateAvatar={candidateAvatar}
        getStepRef={getStepRef}
        activeStep={activeStep}
        onStepChange={onStepChange}
      />

      <ScheduleFooter
        disabled={isBusy || isSaving}
        canGoBack={canGoBack}
        backButtonLabel={backButtonLabel}
        primaryButtonLabel={primaryButtonLabel}
        onBack={() => void goBack()}
        onCancel={() => onClose?.()}
        onPrimary={() => void goNextOrComplete()}
        secondaryCta={secondaryCta}
      />
    </Flex>
  );
};
